/**
 * Vanturis Passport Phase 8 — dedicated Passport application shell.
 *
 * Passport-owned header/nav/footer + protected-route handling.  Reuses
 * shared Vanturis auth (ONE account) but depends on no Marketplace page
 * shell, navigation state, or listing components.
 */
import { useEffect, useState, Suspense } from "react";
import { Link, NavLink, Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Menu, X, ChevronDown, ShieldCheck, LogOut, ArrowLeftRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { pp, marketplaceHome, isPassportHost } from "@/passport/product";
import { passportApi, passportErrorKind } from "@/passport/api";
import PassportComingSoon from "@/passport/components/PassportComingSoon";
import Logo from "@/components/Logo";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", testid: "passport-nav-dashboard" },
  { to: "/shared", label: "Shared With Me", testid: "passport-nav-shared" },
  { to: "/claims", label: "Claims", testid: "passport-nav-claims" },
];

function ProductSwitcher() {
  const [open, setOpen] = useState(false);
  const mkHome = marketplaceHome();
  const external = isPassportHost();
  return (
    <div className="relative"
         onMouseEnter={() => setOpen(true)}
         onMouseLeave={() => setOpen(false)}>
      <button onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-neutral-400 hover:text-[#C6A15B] transition-colors"
              aria-haspopup="true" aria-expanded={open}
              data-testid="passport-product-switcher">
        <ArrowLeftRight className="w-3.5 h-3.5" strokeWidth={2} />
        Products <ChevronDown className="w-3 h-3" strokeWidth={2} />
      </button>
      {open && (
        <div className="absolute top-full right-0 pt-3 min-w-[210px] z-50"
             data-testid="passport-product-switcher-menu">
          <div className="bg-neutral-900 border border-neutral-800 shadow-xl p-2">
            {external ? (
              <a href={mkHome}
                 className="block py-2.5 px-3 text-[11px] tracking-[0.2em] uppercase text-neutral-400 hover:text-[#C6A15B]"
                 data-testid="passport-switch-marketplace">Vanturis Marketplace</a>
            ) : (
              <Link to={mkHome}
                    className="block py-2.5 px-3 text-[11px] tracking-[0.2em] uppercase text-neutral-400 hover:text-[#C6A15B]"
                    data-testid="passport-switch-marketplace">Vanturis Marketplace</Link>
            )}
            <Link to={pp("/")}
                  className="block py-2.5 px-3 text-[11px] tracking-[0.2em] uppercase text-[#C6A15B]"
                  data-testid="passport-switch-passport">Vanturis Passport</Link>
          </div>
        </div>
      )}
    </div>
  );
}

function AccountControls() {
  const { user, logout } = useAuth();
  if (user === null) return null;
  if (!user) {
    return (
      <div className="flex items-center gap-3">
        <Link to={pp("/login")}
              className="text-[11px] uppercase tracking-[0.22em] text-neutral-300 hover:text-[#C6A15B] transition-colors"
              data-testid="passport-nav-signin">Sign In</Link>
        <Link to={pp("/signup")}
              className="text-[11px] uppercase tracking-[0.22em] px-4 py-2 border border-[#C6A15B]/60 text-[#C6A15B] hover:bg-[#C6A15B] hover:text-neutral-950 transition-colors"
              data-testid="passport-nav-signup">Create Account</Link>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-4">
      <span className="hidden md:block text-xs text-neutral-500 truncate max-w-[180px]"
            data-testid="passport-account-email">{user.email}</span>
      <button onClick={logout}
              className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-[#C6A15B] transition-colors"
              data-testid="passport-signout-btn">
        <LogOut className="w-3.5 h-3.5" /> Sign Out
      </button>
    </div>
  );
}

export function PassportShell({ children, noindex = true }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col">
      {noindex && (
        <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      )}
      <header className="sticky top-0 z-40 bg-neutral-950/90 backdrop-blur-md border-b border-neutral-800"
              data-testid="passport-shell-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to={pp("/")} className="flex items-center gap-2.5 min-w-0" data-testid="passport-logo-link">
            <Logo variant="master-gold" height={34} ariaLabel="Vanturis Passport" />
            <span className="text-xs tracking-[0.28em] uppercase whitespace-nowrap text-[#C6A15B] border-l border-white/15 pl-2.5">
              Passport
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-6" data-testid="passport-desktop-nav">
            {user && NAV_ITEMS.map((it) => (
              <NavLink key={it.to} to={pp(it.to)} data-testid={it.testid}
                       className={({ isActive }) =>
                         `text-[11px] uppercase tracking-[0.22em] transition-colors ${
                           isActive ? "text-[#C6A15B]" : "text-neutral-400 hover:text-neutral-100"}`}>
                {it.label}
              </NavLink>
            ))}
            <ProductSwitcher />
            <AccountControls />
          </nav>
          <button className="md:hidden text-neutral-300" onClick={() => setMobileOpen((v) => !v)}
                  aria-label="Toggle navigation" data-testid="passport-mobile-menu-btn">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-neutral-800 bg-neutral-950 px-4 py-4 space-y-1"
               data-testid="passport-mobile-nav">
            {user && NAV_ITEMS.map((it) => (
              <NavLink key={it.to} to={pp(it.to)} onClick={() => setMobileOpen(false)}
                       className="block py-2.5 text-[12px] uppercase tracking-[0.2em] text-neutral-300 hover:text-[#C6A15B]"
                       data-testid={`${it.testid}-mobile`}>
                {it.label}
              </NavLink>
            ))}
            {isPassportHost() ? (
              <a href={marketplaceHome()} className="block py-2.5 text-[12px] uppercase tracking-[0.2em] text-neutral-500 hover:text-[#C6A15B]"
                 data-testid="passport-switch-marketplace-mobile">Vanturis Marketplace</a>
            ) : (
              <Link to={marketplaceHome()} onClick={() => setMobileOpen(false)}
                    className="block py-2.5 text-[12px] uppercase tracking-[0.2em] text-neutral-500 hover:text-[#C6A15B]"
                    data-testid="passport-switch-marketplace-mobile">Vanturis Marketplace</Link>
            )}
            <div className="pt-3 border-t border-neutral-800">
              <AccountControls />
            </div>
          </div>
        )}
      </header>
      <main className="flex-1">
        <Suspense
          fallback={
            <div
              className="max-w-6xl mx-auto px-6 py-24 animate-pulse text-neutral-600"
              aria-hidden="true"
              data-testid="passport-content-fallback"
            />
          }
        >
          {children}
        </Suspense>
      </main>
      <footer className="border-t border-neutral-800 bg-neutral-950" data-testid="passport-shell-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-xs text-neutral-500 tracking-wide">
            © {new Date().getFullYear()} Vanturis · Vanturis Passport — permanent history for specialty assets.
          </div>
          <div className="flex items-center gap-5 text-[11px] uppercase tracking-[0.18em] text-neutral-500">
            <Link to={pp("/")} className="hover:text-[#C6A15B]" data-testid="passport-footer-about">About Passport</Link>
            <Link to="/legal/privacy" className="hover:text-[#C6A15B]" data-testid="passport-footer-privacy">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-[#C6A15B]" data-testid="passport-footer-terms">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** Protected wrapper: unauthenticated users are sent to the Passport
 *  sign-in with a validated internal returnTo.  Authenticated users hit a
 *  single customer-dark gate — while PASSPORT_ENABLED=false every customer
 *  route answers 404, so all protected pages show one "coming soon" gate
 *  instead of live-looking empty states. */
export function PassportProtected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [gate, setGate] = useState("checking"); // checking | open | dark

  useEffect(() => {
    let alive = true;
    if (loading || !user) return; // wait until auth resolves
    setGate("checking");
    passportApi.capabilities()
      .then(() => alive && setGate("open"))
      // Only an explicit availability 404 darkens the surface; transient
      // errors fail open so a live customer is never wrongly gated.
      .catch((err) => alive && setGate(passportErrorKind(err) === "unavailable" ? "dark" : "open"));
    return () => { alive = false; };
  }, [user, loading]);

  if (loading || user === null || (user && gate === "checking")) {
    return (
      <PassportShell>
        <div className="max-w-6xl mx-auto px-6 py-24 animate-pulse text-neutral-500"
             data-testid="passport-protected-loading">Loading Passport…</div>
      </PassportShell>
    );
  }
  if (!user) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${pp("/login")}?returnTo=${returnTo}`} replace />;
  }
  if (gate === "dark") {
    return <PassportShell><PassportComingSoon /></PassportShell>;
  }
  return <PassportShell>{children}</PassportShell>;
}
